import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';

/* ── Types ── */

export interface InvoiceTotals {
  icmsTotal: number;
  pisTotal: number;
  cofinsTotal: number;
  ipiTotal: number;
  issTotal: number;
  totalValue: number;
  discount: number;
  shipping: number;
}

export interface InvoiceItem {
  id: string;
  description: string;
  ncm: string;
  cfop: string;
  cst: string;
  quantity: number;
  unitValue: number;
  totalValue: number;
  icmsValue: number;
  pisValue: number;
  cofinsValue: number;
  ipiValue: number;
  issValue: number;
}

export interface Invoice {
  id: string;
  type: string;
  status: string;
  /** `null` enquanto a nota nao foi autorizada — o DTO declara `string | null`. */
  accessKey: string | null;
  protocolNumber: string | null;
  emissionDate: string;
  authorizationDate: string | null;
  rejectionReason: string | null;
  totals: InvoiceTotals;
  items: InvoiceItem[];
  emitter: { companyName: string; cnpj: string };
  destinatary: { name: string; cnpj?: string; cpf?: string };
  /* `events` nao vem de `/invoices`: nenhum endpoint expoe eventos fiscais e
     nada grava `FiscalEvent` hoje. Fica opcional e sem fonte — a tela mostra
     "Nenhum evento registrado", que e o estado real. Quando a emissao passar a
     registrar evento, e so ligar a fonte. */
  events?: Array<{ id: string; tipo: string; descricao: string; data: string; usuario: string }>;
}

/** Contagem por status que acompanha a listagem (`InvoiceCountsDto`). */
export interface InvoiceCounts {
  authorized: number;
  pending: number;
  rejected: number;
  cancelled: number;
}

export interface InvoiceList extends PaginatedResponse<Invoice> {
  counts: InvoiceCounts;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface EmitInvoiceItemInput {
  productId?: string;
  productCode: string;
  productName: string;
  ncmCode: string;
  cestCode?: string;
  cfopCode?: string;
  uom: string;
  quantity: number;
  unitPrice: number;
  discount?: number;
  eanCode?: string;
  additionalProductInfo?: string;
}

export interface EmitInvoiceAddressInput {
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
  country?: string;
  ibgeCode?: string;
}

export interface EmitInvoiceEmitterInput {
  cnpj: string;
  ie: string;
  companyName: string;
  tradeName?: string;
  address: EmitInvoiceAddressInput;
  crt?: '1' | '2' | '3' | '4';
}

export interface EmitInvoiceDestinataryInput {
  cnpj?: string;
  cpf?: string;
  name: string;
  address: EmitInvoiceAddressInput;
  ie?: string;
  email?: string;
  indIEDest?: '1' | '2' | '9';
}

export interface EmitInvoicePaymentInput {
  detPag: Array<{ tPag: string; vPag: number; xPag?: string; indPag?: '0' | '1' }>;
  vTroco?: number;
}

export interface EmitInvoiceInput {
  companyId: string;
  branchId: string;
  type: string;
  ide?: { cUF?: string; serie?: number; nNF?: number; natOp?: string; tpAmb?: '1' | '2' };
  emitter: EmitInvoiceEmitterInput;
  destinatary: EmitInvoiceDestinataryInput;
  items: EmitInvoiceItemInput[];
  operationType: string;
  regimeTributario: string;
  discount?: number;
  shipping?: number;
  insurance?: number;
  otherFees?: number;
  payment?: EmitInvoicePaymentInput;
  additionalInfo?: { infCpl?: string; infAdFisco?: string };
}

export interface CancelInvoiceInput {
  reason: string;
  justification: string;
}

export interface Party {
  id: string;
  name: string;
  cnpj?: string;
  cpf?: string;
  ie?: string;
  email?: string;
  address?: EmitInvoiceAddressInput;
}

/* Espelha `ProductResponseDto`: o identificador e `sku` e o NCM e `ncmCode`.
   Lendo `code`/`ncm`, o item da nota saia sem codigo e sem NCM — e NCM vazio
   reprova na validacao da SEFAZ. */
export interface Product {
  id: string;
  name: string;
  sku: string;
  ncmCode?: string;
  price: number;
  unit: string;
}

export interface Company {
  id: string;
  name: string;
  cnpj: string;
  tradeName?: string;
  ie?: string;
  crt?: '1' | '2' | '3' | '4';
  address?: EmitInvoiceAddressInput;
}

export interface Branch {
  id: string;
  tenantId: string;
  companyId: string;
  code: string;
  name: string;
  email?: string;
  phone?: string;
  status: string;
}

export interface NumberSequence {
  id: string;
  tenantId: string;
  companyId: string;
  branchId?: string;
  entity: string;
  format: string;
  currentSequence: number;
  startNumber: number;
  endNumber?: number;
  prefix?: string;
  suffix?: string;
  isActive: boolean;
}

/* ── Hooks ── */

export function useFiscalInvoices(
  tenantId: string,
  params?: { type?: string; status?: string; accessKey?: string; page?: number; limit?: number },
) {
  return useQuery({
    queryKey: [...queryKeys.fiscal.list(tenantId), params],
    queryFn: () =>
      apiClient.get<InvoiceList>('/invoices', {
        params: {
          ...(params?.page ? { page: String(params.page) } : {}),
          ...(params?.limit ? { limit: String(params.limit) } : {}),
          ...(params?.type && params.type !== 'ALL' ? { type: params.type } : {}),
          ...(params?.status && params.status !== 'ALL' ? { status: params.status } : {}),
          ...(params?.accessKey ? { accessKey: params.accessKey } : {}),
        },
      }),
    enabled: !!tenantId,
  });
}

export function useFiscalInvoice(tenantId: string, id: string) {
  return useQuery({
    queryKey: queryKeys.fiscal.detail(tenantId, id),
    queryFn: () => apiClient.get<Invoice>(`/invoices/${id}`),
    enabled: !!id && !!tenantId,
  });
}

export function useEmitFiscalInvoice(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EmitInvoiceInput) =>
      apiClient.post<{ invoice: Invoice; accessKey: string; taxResults: unknown; sefazResult: unknown; cfopUsed: string }>('/invoices/emit', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.fiscal.list(tenantId) }),
  });
}

/**
 * Espelha `EmissionRequestResponse` (emission-requests.controller.ts).
 *
 * `hasXml` e presenca, nao conteudo: o XML autorizado e um blob e a fila
 * devolve ate 50 linhas.
 */
export interface EmissionRequest {
  id: string;
  fiscalDocumentId: string;
  status: string;
  providerType: string;
  sefazState: string | null;
  authorizationProtocol: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  danfeUrl: string | null;
  hasXml: boolean;
  attempts: number;
  nextRetryAt: string | null;
  processedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EmissionRequestList {
  data: EmissionRequest[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/** Situacoes em que a emissao ainda esta se movendo. */
const EM_ANDAMENTO = new Set(['QUEUED', 'PENDING', 'PROCESSING', 'RETRYING', 'SIGNING', 'TRANSMITTING']);

export function isEmissionSettled(request: EmissionRequest | undefined): boolean {
  if (!request) return false;
  return !EM_ANDAMENTO.has(request.status.toUpperCase());
}

/**
 * Requisicoes de emissao de UM documento.
 *
 * O filtro `fiscalDocumentId` usa o indice que o schema tem para isso; antes
 * dele seria preciso puxar a fila inteira. Enquanto a emissao nao assenta, a
 * consulta se repete — e o intervalo para quando ela assenta, para nao ficar
 * batendo no servidor para sempre.
 */
export function useEmissionRequests(fiscalDocumentId: string | null) {
  return useQuery({
    queryKey: ['fiscal', 'emission-requests', fiscalDocumentId] as const,
    queryFn: () =>
      apiClient.get<EmissionRequestList>('/fiscal/emission-requests', {
        params: { fiscalDocumentId: fiscalDocumentId ?? '', limit: '10' },
      }),
    enabled: !!fiscalDocumentId,
    refetchInterval: (query) => {
      const atual = query.state.data?.data[0];
      return isEmissionSettled(atual) ? false : 4000;
    },
  });
}

export function useCancelFiscalInvoice(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason, justification }: { id: string; reason: string; justification: string }) =>
      apiClient.post(`/invoices/${id}/cancel`, { reason, justification }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.fiscal.list(tenantId) });
      qc.invalidateQueries({ queryKey: queryKeys.fiscal.detail(tenantId, variables.id) });
    },
  });
}

export function useFetchParties(tenantId: string, search: string) {
  return useQuery({
    queryKey: [...queryKeys.masterData.parties(tenantId), search],
    queryFn: () =>
      apiClient.get<{ data: Party[]; total: number }>('/parties', {
        params: { search, limit: '10' },
      }),
    enabled: !!tenantId && search.length >= 3,
  });
}

export function useFetchProducts(tenantId: string, search: string) {
  return useQuery({
    queryKey: [...queryKeys.masterData.products(tenantId), search],
    queryFn: () =>
      /* `GET /products` devolve array puro — e o que StockPage, ProductsPage e
         o catalogo offline do PDV consomem. Este hook esperava envelope e
         recebia `undefined` em `.data`: a busca de produto do item da nota
         nunca achava nada. */
      apiClient.get<Product[]>('/products', {
        params: { search, limit: '10' },
      }),
    enabled: !!tenantId && search.length >= 3,
  });
}

export function useFetchCompanies(tenantId: string) {
  return useQuery({
    queryKey: [...queryKeys.tenant.current(tenantId), 'companies'],
    // Contrato real: GET /api/v1/tenants/:tenantId/companies (array direto)
    queryFn: async (): Promise<{ data: Company[] }> => {
      const res = await apiClient.get<Company[] | { data: Company[] }>(
        `/tenants/${tenantId}/companies`,
      );
      if (Array.isArray(res)) return { data: res };
      return res;
    },
    enabled: !!tenantId,
  });
}

export function useFetchBranches(companyId: string) {
  return useQuery({
    queryKey: [...queryKeys.organization.branches(), companyId] as any,
    queryFn: () =>
      apiClient.get<Branch[]>(`/companies/${companyId}/branches`),
    enabled: !!companyId,
  });
}

export function useFetchNumberSequences(tenantId: string, companyId: string) {
  return useQuery({
    queryKey: [...queryKeys.organization.numberSequences(), tenantId, companyId] as any,
    queryFn: () =>
      apiClient.get<NumberSequence[]>(`/companies/${companyId}/number-sequences`, {
        params: { tenantId },
      }),
    enabled: !!tenantId && !!companyId,
  });
}

/** Gera o próximo número (sequência) para uma entidade — retorna o número formatado. */
export async function generateNextNumber(sequenceId: string, branchCode?: string): Promise<{ generatedNumber: string; currentSequence: number }> {
  return apiClient.post<{ generatedNumber: string; currentSequence: number }>(
    `/number-sequences/${sequenceId}/generate`,
    undefined,
    { params: branchCode ? { branchCode } : undefined },
  );
}
